import { useEditor } from "@grapesjs/react";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useMemo } from "react";
import { Selector } from "grapesjs";

interface ILightningLanderRefreshButtonProps {
  selectors: Selector[];
}
const LightningLanderRefreshButton = ({
  selectors: allSelectors,
}: ILightningLanderRefreshButtonProps) => {
  const editor = useEditor();
  const theme = useTheme();
  const selectors = useMemo(
    () => (allSelectors || []).filter((s) => s.getActive()),
    [allSelectors]
  );
  const selName = useMemo(
    () => selectors.map((s) => s.toString()).join(""),
    [selectors]
  );
  const comp = editor.getSelected();
  const wrapper = editor.DomComponents.getWrapper();
  const allWithClass = useMemo(() => {
    if (!wrapper) return [];
    if (!selName) return [];
    return wrapper?.find(selName) || [];
  }, [wrapper, selName]);

  const handleRefresh = useCallback(() => {
    if (!editor) return null;
    if (!comp || !selectors || selectors.length === 0) return null;
    const stylesToSync = { ...comp?.getStyle() };
    const propsToSync = Object.keys(stylesToSync);
    if (propsToSync.length === 0) return;

    // 2) Merge them into the shared CSS rule
    editor.Css.setRule(selName, stylesToSync, { addStyles: true });

    allWithClass?.forEach((c) => {
      // 4) For each one, remove only the synced props
      propsToSync.forEach((prop) => c.removeStyle(prop));

      // 5) Remove any ID-scoped CSS rule for that component
      const id = c.getId();
      if (id) editor.Css.remove(`#${id}`);
    });

    // 6) Re-render so the canvas picks up the stylesheet changes
    editor.render();

    // small hack so react sees change
    editor.selectRemove(comp);
    editor.select(comp);
  }, [editor, selName, selectors, comp, allWithClass]);

  if (
    !selectors ||
    selectors.length === 0 ||
    Object.keys(comp?.getStyle() || {}).length === 0
  ) {
    return <></>;
  }

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Typography variant="subtitle2">
        Apply{" "}
        <Tooltip
          title={JSON.stringify(comp?.getStyle(), null, 2)}
          placement="left"
          arrow
        >
          <Box
            sx={{
              display: "inline-block",
              textAlign: "center",
              cursor: "help",
              border: `1px solid ${theme.palette.primary.vibrant}`,
              borderRadius: "50%",
              height: 23,
              width: 23,
            }}
          >
            <Typography
              display={"inline"}
              component="strong"
              color={"primary.vibrant"}
              fontWeight={600}
            >
              {Object.keys(comp?.getStyle() || {}).length}
            </Typography>
          </Box>
        </Tooltip>{" "}
        Change {Object.keys(comp?.getStyle() || {}).length > 1 && "s"} To{" "}
        {(allWithClass || []).length} Components with a class{" "}
        <Typography
          display={"inline"}
          component="span"
          color={"primary.vibrant"}
        >
          {selName}
        </Typography>{" "}
      </Typography>
      <Button
        fullWidth
        size="small"
        onClick={handleRefresh}
        title="Sync to CSS rule"
        startIcon={<RefreshIcon fontSize="small" />}
      >
        Apply
      </Button>
    </Stack>
  );
};

export default LightningLanderRefreshButton;
